/** Scene 1: «Таблица Сивцева» — a backlit eye chart at 5 m and a reading card at 40 cm (docs/SCENES.md §1). */

import { tl, type Lang } from '../i18n';
import { fitFont, geom, roundRectPath, setFont, withShadow } from './draw-utils';
import { RENDER_H, RENDER_W, type Scene } from './types';

const HFOV = 10;
const G = geom(HFOV);

/** 5 arcmin at this ppd: the cap height of a VA = 1.0 letter. */
const CAP_AT_VA1 = (5 / 60) * G.ppd; // 9.6 px

const CHART_ROWS: ReadonlyArray<readonly [number, string]> = [
  [0.1, 'ШБ'],
  [0.2, 'МНК'],
  [0.3, 'ЫМБШ'],
  [0.4, 'БЫНК'],
  [0.5, 'ИНШМК'],
  [0.6, 'НКИБМШ'],
  [0.7, 'ШИНБКЫ'],
  [0.8, 'КНШМЫБИ'],
  [0.9, 'БКШМИЫН'],
  [1.0, 'НШЫИКБМ'],
  [1.5, 'ИНКШМЫБШ'],
  [2.0, 'БМНЫШИКН'],
];

const NEAR_VAS = [0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 1.0];

function capFor(va: number): number {
  return CAP_AT_VA1 / va;
}

function drawFar(ctx: CanvasRenderingContext2D): void {
  // Clinic wall: light grey-blue, slightly darker at the bottom.
  const wall = ctx.createLinearGradient(0, 0, 0, RENDER_H);
  wall.addColorStop(0, '#e3e9ef');
  wall.addColorStop(1, '#c8d2db');
  ctx.fillStyle = wall;
  ctx.fillRect(0, 0, RENDER_W, RENDER_H);

  // Chart panel geometry.
  const caps = CHART_ROWS.map(([va]) => capFor(va));
  const rowGap = 0.55;
  let contentH = 0;
  caps.forEach((cap, i) => {
    contentH += cap;
    if (i < caps.length - 1) contentH += cap * rowGap;
  });
  const padY = 34;
  const panelW = 420;
  const panelH = contentH + 2 * padY;
  const cx = G.X(-2.6);
  const cy = G.Y(0);
  const px = cx - panelW / 2;
  const py = cy - panelH / 2;

  withShadow(ctx, 'rgba(20, 30, 40, 0.35)', 40, 12, () => {
    ctx.fillStyle = '#f7f9fb';
    ctx.fillRect(px, py, panelW, panelH);
  });
  // Backlit glow: brighter centre.
  const glow = ctx.createRadialGradient(cx, cy, 40, cx, cy, panelH * 0.7);
  glow.addColorStop(0, '#ffffff');
  glow.addColorStop(1, '#eef2f5');
  ctx.fillStyle = glow;
  ctx.fillRect(px, py, panelW, panelH);
  ctx.strokeStyle = '#aeb9c4';
  ctx.lineWidth = 3;
  ctx.strokeRect(px + 1.5, py + 1.5, panelW - 3, panelH - 3);

  // Rows.
  ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = '#0b0f14';
  let y = py + padY;
  CHART_ROWS.forEach(([va, letters], i) => {
    const cap = caps[i]!;
    const baseline = y + cap;
    setFont(ctx, cap, 700);
    const widths = [...letters].map((ch) => ctx.measureText(ch).width);
    const gap = cap * 0.6;
    const total = widths.reduce((a, b) => a + b, 0) + gap * (letters.length - 1);
    let x = cx - total / 2;
    ctx.fillStyle = '#0b0f14';
    ctx.textAlign = 'left';
    [...letters].forEach((ch, k) => {
      ctx.fillText(ch, x, baseline);
      x += widths[k]! + gap;
    });
    // Side labels: D = 5 / VA metres on the left, V on the right.
    ctx.font = `500 11px system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif`;
    ctx.fillStyle = '#5b6873';
    const labelY = baseline - Math.min(cap, 11) * 0.15;
    ctx.textAlign = 'left';
    ctx.fillText(`D=${formatD(5 / va)}`, px + 12, labelY);
    ctx.textAlign = 'right';
    ctx.fillText(`V=${va.toFixed(1)}`, px + panelW - 12, labelY);
    y = baseline + cap * rowGap;
  });
  ctx.textAlign = 'left';
}

function formatD(m: number): string {
  return Number.isInteger(m) ? String(m) : m.toFixed(1);
}

function drawNear(ctx: CanvasRenderingContext2D, lang: Lang): void {
  const x0 = G.X(0.3);
  const x1 = G.X(4.9);
  const y0 = G.Y(3);
  const y1 = G.Y(-3);
  const w = x1 - x0;
  const h = y1 - y0;

  withShadow(ctx, 'rgba(10, 20, 30, 0.35)', 28, 10, () => {
    roundRectPath(ctx, x0, y0, w, h, 14);
    ctx.fillStyle = '#ffffff';
    ctx.fill();
  });
  roundRectPath(ctx, x0, y0, w, h, 14);
  ctx.strokeStyle = '#d5dbe1';
  ctx.lineWidth = 1.5;
  ctx.stroke();

  const lines = tl(lang, 'chart.near');
  const padL = 30;
  const padR = 20;
  const textX = x0 + padL;
  const maxW = w - padL - padR;
  let y = y0 + 58;
  ctx.textBaseline = 'alphabetic';
  ctx.textAlign = 'left';
  NEAR_VAS.forEach((va, i) => {
    const text = lines[i] ?? '';
    const cap = capFor(va);
    // The largest line may shrink slightly to fit the card width; its label stays nominal.
    fitFont(ctx, text, cap, maxW, 400);
    const baseline = y + cap;
    ctx.fillStyle = '#1b2228';
    ctx.fillText(text, textX, baseline);
    ctx.font = `500 9px system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif`;
    ctx.fillStyle = '#8a96a1';
    ctx.fillText(va.toFixed(1), x0 + 8, baseline);
    y = baseline + cap * 1.3 + 10;
  });
}

export const chartScene: Scene = {
  id: 'chart',
  hfov: HFOV,
  lighting: 'chart',
  exposure: 1.0,
  layers: [
    { id: 'far', V: 0.2, draw: (ctx) => drawFar(ctx) },
    { id: 'near', V: 2.5, draw: (ctx, lang) => drawNear(ctx, lang) },
  ],
  lights: () => [],
  // Tags sit at the bottom edges so they never collide with the pane labels in the top corners.
  tags: [
    { x: -2.6, y: -2.5, key: 'tag.far' },
    { x: 2.6, y: -2.75, key: 'tag.near' },
  ],
};
